import AWS from 'aws-sdk';
import { useEffect, useState, useRef } from 'react';
// import { Socket, io } from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import socket, { getExpirationTime } from '../socket';
import { UserChatResponse } from '../types/chat.types';
import { AppDispatch, RootState } from '../redux/store';
import {
  CONNECTED,
  CONNECTING,
  DISCONNECTED,
} from '../redux/reducers/socketConnectionReducer';
import { ADD_EVENT, ADD_LOADER_EVENT, CLEAR_EVENTS, REMOVE_LOADER_EVENT } from '../redux/reducers/eventReducer';
import { globalCards } from '../utils/global.card';
import getTimeStamp from '../utils/getTimeStamp';
import { extractLinkInfo } from '../utils/convertLinkToHyperLink';
import { S3_BUCKET } from '../constants/global.constants';
import { generateRandomId } from '../utils/global.utils';
import safelyParseJSON from '../utils/safelyParseJson';
import { playSound } from '../utils/playNotification';


// Configure AWS with your credentials
AWS.config.update({
  accessKeyId: S3_BUCKET.ACCESS_KEY_ID,
  secretAccessKey: S3_BUCKET.ACCESS_KEY_SECRET,
  region: S3_BUCKET.REGION,
});

function useSocket() {
  const auth = useSelector((state: RootState) => state.auth.auth);
  const isChatOpen = useSelector(
    (state: RootState) => state.chatScreen.isChatInitiatedByUser,
  );
  // const events = useSelector((state: RootState) => state.events);

  const dispatch = useDispatch<AppDispatch>();
  const isMute = useSelector((state: RootState) => state.audio?.mute ?? false)

  const [isConnected, setIsConnected] = useState(socket.connected);
  const lastCardEventRef = useRef<{ id: string; timestamp: number } | null>(null);
  const lastChatEventRef = useRef<{ id: string; timestamp: number } | null>(null);
  const lastBotChatEventRef = useRef<{ id: string; timestamp: number } | null>(null);

  function onConnect() {
    setIsConnected(true);
    dispatch({
      type: CONNECTED,
      payload: { status: 'Connected', socketId: socket.id, socket },
    });
    // dispatch(toggleChatConnected({ chatConnected: true, socket: socket }));
  }

  useEffect(() => {
    function onConnecting() {
      setIsConnected(true);
      dispatch({ type: CONNECTING, payload: { status: 'Connecting' } });
    }

    function onDisconnect() {
      setIsConnected(false);
      // localStorage.removeItem('x-session-key');
      // localStorage.removeItem('userNumber');
      //localStorage.removeItem('recentEvents');
      dispatch({ type: DISCONNECTED, payload: { status: 'Disconnected' } });
    }


    function onUpdateChatEvent(
      username: string,
      data: UserChatResponse | string | any,
      divId = null,
    ) {
      if (data) {
        const timeStamp = getTimeStamp(data.updatedAt);
        if (data.senderType === 'USER' || data.senderType === 'SUPPORT') {
          const dataTextInfo =
            data.senderType === 'SUPPORT'
              ? extractLinkInfo(data.text)
              : { linkText: data.text, url: '' };
          dispatch({
            type: ADD_EVENT,
            payload: {
              type: 'chat',
              id: data.id,
              user: data.senderId,
              time: timeStamp,
              senderType: data.senderType,
              msg: dataTextInfo.linkText,
              url: dataTextInfo.url,
              divId: data.id,
            },
          });
          if (data.senderType === 'USER') {
            dispatch({
              type: ADD_LOADER_EVENT,
              payload: {
                type: 'loader',
                id: `loader-${Date.now()}`,
                senderType: 'Chat bot',
                time: timeStamp,
              },
            });
          }
          return;
        } else {
          dispatch({
            type: ADD_EVENT,
            payload: {
              type: 'chat',
              id: divId,
              user: username,
              time: timeStamp,
              senderType: username,
              msg: data,
              divId,
            },
          });
        }
      }
      if (data.senderType !== "USER" && !isMute) {
        playSound()
      }
      // scrollToBottom();
    }

    function onUpdateCard(cardEvent: any) {
      const currentTime = Date.now();
      const eventId = JSON.stringify(cardEvent);
      if (
        lastCardEventRef.current &&
        lastCardEventRef.current.id === eventId &&
        currentTime - lastCardEventRef.current.timestamp < 1000 // 1 second threshold
      ) {
        return;
      }

      lastCardEventRef.current = {
        id: eventId,
        timestamp: currentTime,
      };

      const { payload, card_message = '' } = cardEvent;
      const card = globalCards[payload.actionType as string];
      const timeStamp = getTimeStamp();
      if (card_message) {
        const sender = 'Chat Bot';
        const divId = generateRandomId();
        dispatch({type: REMOVE_LOADER_EVENT });
        dispatch({
          type: ADD_EVENT,
          payload: {
            type: 'chat',
            id: divId,
            divId,
            user: sender,
            time: timeStamp,
            senderType: sender,
            msg: card_message,
          },
        });
      }
      if (card?.auth && auth) {
        const loginCard = globalCards['existingUserSignCard'];
        dispatch({type: REMOVE_LOADER_EVENT });
        const divId = generateRandomId();
        dispatch({
          type: ADD_EVENT,
          payload: {
            ...loginCard,
            id: divId,
            divId,
            user: 'actionCard',
            time: timeStamp,
            senderType: 'chatBot',
            actionType: payload.actionType,
            // isMenuCard: payload.actionType === 'menuCard',
          },
        });
      } else {
        dispatch({type: REMOVE_LOADER_EVENT });
        const divId = generateRandomId();
        dispatch({
          type: ADD_EVENT,
          payload: {
            ...card,
            id: divId,
            divId,
            user: 'actionCard',
            time: timeStamp,
            senderType: 'chatBot',
            actionType: payload.actionType,
            title: payload.title
            // isMenuCard: payload.actionType === 'menuCard',
          },
        });
      }
      if(!isMute) {
        playSound();
      }
    }

    function onSupportEndChat() {
      socket.disconnect();
      localStorage.clear();
      dispatch({ type: CLEAR_EVENTS });
    }

    function onBotTriggerChat(username: string,
      data: UserChatResponse | string | any,
    ) {
      const eventId = typeof data === 'object' && data.id ? data.id : JSON.stringify(data);
      const currentTime = Date.now();
      if (
        lastBotChatEventRef.current &&
        lastBotChatEventRef.current.id === eventId &&
        currentTime - lastBotChatEventRef.current.timestamp < 1000
      ) {
        return;
      }
      lastBotChatEventRef.current = {
        id: eventId,
        timestamp: currentTime,
      };

      const timeStamp = getTimeStamp(data.updatedAt);
      dispatch({
        type: ADD_EVENT,
        payload: {
          type: 'chat',
          id: data.id,
          user: data.senderId,
          time: timeStamp,
          senderType: data.senderType,
          msg: data.text,
          divId: data.id,
        },
      });
    }

    socket.on('connect', onConnect);
    socket.on('connecting', onConnecting);
    socket.on('disconnect', onDisconnect);
    socket.on('socket_disconnect', onSupportEndChat);
    socket.on('connect_error', onDisconnect);
    socket.on('connect_timeout', onDisconnect);
    socket.on('updatechat', onUpdateChatEvent);
    socket.on('card', onUpdateCard);
    socket.on('support_end_chat_success', onSupportEndChat);
    socket.on('bot_trigger_chat', onBotTriggerChat)

    return () => {
      socket.off('connect', onConnect);
      socket.off('connecting', onConnecting);
      socket.off('disconnect', onDisconnect);
      socket.off('socket_disconnect', onSupportEndChat);
      socket.off('connect_error', onDisconnect);
      socket.off('connect_timeout', onDisconnect);
      socket.off('updatechat', onUpdateChatEvent);
      socket.off('card', () => { });
      socket.off('support_end_chat_success', onSupportEndChat);
      socket.off('bot_trigger_chat', onBotTriggerChat);
      lastCardEventRef.current = null;
      lastChatEventRef.current = null;
      lastBotChatEventRef.current = null;
    };
  }, [auth, dispatch, isChatOpen]);

  useEffect(() => {
    // Function to be executed when the tab is closed
    const handleTabClose = () => {
      const expirationTime = getExpirationTime() as string;
      const disconnecting = !!safelyParseJSON(
        localStorage.getItem('disconnecting') as string,
      );
      // Remove the key from localStorage only if it's the last tab
      if (
        new Date().getTime() > parseInt(expirationTime, 10) ||
        disconnecting
      ) {
        // localStorage.removeItem('x-session-key'); // Replace 'yourKey' with the actual key you want to remove
        localStorage.clear();
      }
    };

    window.addEventListener('beforeunload', handleTabClose);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, []);

  return {
    isConnected,
  };
}

export default useSocket;
